import React, { useEffect, useMemo, useState } from "react";
import {
  buildCategoriasEndpoint,
  extractCategoriasFromReport,
  getStatusChipProps,
  getSummary,
  sortCategorias,
} from "./categorias.utils";
import CategoriaModal from "./CategoriaModal";

// ─── Card de una categoría ────────────────────────────────────────────────────
const CategoriaCard = ({ categoria, idsport }) => {   
  const [modalOpen, setModalOpen] = useState(false);

  return (
    <>
      <div
        onClick={() => setModalOpen(true)}
        className="h-full rounded-2xl border border-gray-200 bg-white shadow-sm cursor-pointer
                   transition-all duration-200 ease-in-out
                   hover:-translate-y-0.5 hover:border-blue-500 hover:shadow-md"
      >
        <div className="p-4 flex flex-col gap-4">
          {/* Nombre */}
          <div className="flex items-start justify-between gap-2">
            <h3 className="text-base font-bold text-gray-900 leading-tight">
              {categoria.categoryName}
            </h3>
          </div>

          {/* CTA */}
          <span className="text-xs font-semibold text-blue-600">
            Ver llaves y resultados →
          </span>
        </div>
      </div>

      <CategoriaModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        categoria={categoria}
        idsport={idsport}         
      />
    </>
  );
};

// ─── Componente principal ─────────────────────────────────────────────────────
const Categorias = ({ idsport }) => {
  const [categorias, setCategorias] = useState([]);
  const [eventInfo, setEventInfo]   = useState(null);
  const [sportInfo, setSportInfo]   = useState(null);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState("");

  useEffect(() => {
    let isMounted = true;

    const loadCategorias = async () => {
      setLoading(true);
      setError("");

      try {
        const response = await fetch(buildCategoriasEndpoint(idsport));

        if (!response.ok)
          throw new Error(`Error ${response.status}: no se pudo obtener categorías`);

        const data   = await response.json();
        const sorted = sortCategorias(extractCategoriasFromReport(data));

        if (!isMounted) return;

        setCategorias(sorted);
        setEventInfo(data?.event       || null);
        setSportInfo(data?.sports?.[0] || null);
      } catch (err) {
        if (!isMounted) return;
        setError(err.message || "Ocurrió un error al cargar las categorías");
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    loadCategorias();

    return () => { isMounted = false; };
  }, []);

  const summary = useMemo(() => getSummary(categorias), [categorias]);

  // ── Estado: cargando ──────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="min-h-[260px] flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
          <p className="text-sm text-gray-500">Cargando categorías...</p>
        </div>
      </div>
    );
  }

  // ── Estado: error ─────────────────────────────────────────────────────────
  if (error) {
    return (
      <div className="mt-2 flex items-start gap-2 px-4 py-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
        <span>⚠️</span>
        <span>{error}</span>
      </div>
    );
  }

  // ── Estado: sin categorías ────────────────────────────────────────────────
  if (!categorias.length) {
    return (
      <div className="mt-2 flex items-start gap-2 px-4 py-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-700">
        <span>No hay categorías registradas para este deporte.</span>
      </div>
    );
  }

  // ── Estado: datos ─────────────────────────────────────────────────────────
  return (
    <div className="w-full">
      <div className="flex flex-col gap-6">
        {/* Encabezado */}
        <div>
          <h2 className="text-2xl font-bold text-blue-600">Categorías</h2>
        </div>

        {/* Grid de categorías */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {categorias.map((categoria) => (
            <CategoriaCard
              key={categoria.eventCategoryId}
              categoria={categoria}
              idsport={idsport}        
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default Categorias;